import workingDynamoService from './workingDynamoService';

class LinkService {
  constructor() {
    this.links = JSON.parse(localStorage.getItem('links') || '[]');
    this.nextId = parseInt(localStorage.getItem('nextLinkId') || '1');
  }

  saveToStorage() {
    localStorage.setItem('links', JSON.stringify(this.links));
    localStorage.setItem('nextLinkId', this.nextId.toString());
  }

  generateShortCode(length = 6) {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  validateUrl(url) {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  validateCustomSlug(slug) {
    if (!slug) return true; // Optional
    
    const slugRegex = /^[a-zA-Z0-9-_]+$/;
    if (!slugRegex.test(slug)) {
      throw new Error('Custom slug can only contain letters, numbers, hyphens, and underscores');
    }
    
    if (slug.length < 3 || slug.length > 50) {
      throw new Error('Custom slug must be between 3 and 50 characters');
    }

    // Check if slug is already taken
    const existingLink = this.links.find(link => link.shortCode === slug);
    if (existingLink) {
      throw new Error('This custom slug is already taken');
    }

    return true;
  }

  async createLink(linkData, userId) {
    const { originalUrl, domainId, customSlug, title, description, tags, expiresAt, password } = linkData;

    // Validate required fields
    if (!originalUrl) {
      throw new Error('Original URL is required');
    }

    if (!this.validateUrl(originalUrl)) {
      throw new Error('Please enter a valid URL');
    }

    // Validate domain if specified
    let domain = null;
    if (domainId) {
      // Get domain info from domains loaded in memory (simplified)
      const domains = await workingDynamoService.loadDomains();
      domain = domains.find(d => d.id === domainId);
      if (!domain) {
        throw new Error('Selected domain not found');
      }
      if (!domain.isVerified) {
        throw new Error('Domain must be verified before creating links');
      }
    }

    // Validate custom slug if provided
    let shortCode = customSlug;
    if (customSlug) {
      this.validateCustomSlug(customSlug);
    } else {
      // Generate unique short code
      do {
        shortCode = this.generateShortCode();
      } while (this.links.find(link => link.shortCode === shortCode));
    }

    // Validate expiration date
    if (expiresAt && new Date(expiresAt) <= new Date()) {
      throw new Error('Expiration date must be in the future');
    }

    // Validate password
    if (password && password.length < 4) {
      throw new Error('Password must be at least 4 characters long');
    }

    const newLink = {
      id: this.nextId++,
      originalUrl,
      shortCode,
      domainId: domainId || null,
      domain: domain ? domain.domain : 'short.ly', // Store domain name for display
      fullShortUrl: domain ? `https://${domain.domain}/${shortCode}` : `https://short.ly/${shortCode}`,
      title: title || '',
      description: description || '',
      tags: tags || [],
      clickCount: 0,
      uniqueClicks: 0,
      createdBy: userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      expiresAt: expiresAt || null,
      password: password || null,
      isActive: true,
      lastClickAt: null
    };

    this.links.push(newLink);
    this.saveToStorage();

    // Update domain link count if domain specified
    if (domain) {
      // Update domain link count (simplified for now)
      console.log('Domain link count would be updated for:', domain.domain);
    }

    return newLink;
  }

  async getUserLinks(userEmail) {
    // Return links for a specific user
    return this.links.filter(link => link.createdBy === userEmail);
  }

  async getLinks(userId, options = {}) {
    const { search, domain, tags, status, sortBy, sortOrder = 'desc', page = 1, limit = 20 } = options;

    let filtered = [...this.links].filter(link => link.createdBy === userId);

    // Apply search filter
    if (search) {
      const query = search.toLowerCase();
      filtered = filtered.filter(link => 
        link.originalUrl.toLowerCase().includes(query) ||
        link.shortCode.toLowerCase().includes(query) ||
        link.title.toLowerCase().includes(query) ||
        link.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Apply domain filter
    if (domain && domain !== 'all') {
      filtered = filtered.filter(link => 
        domain === 'custom' ? link.domainId : link.domainId === domain
      );
    }

    // Apply tags filter
    if (tags && tags.length > 0) {
      filtered = filtered.filter(link =>
        tags.some(tag => link.tags.includes(tag))
      );
    }

    // Apply status filter
    if (status === 'active') {
      filtered = filtered.filter(link => link.isActive && (!link.expiresAt || new Date(link.expiresAt) > new Date()));
    } else if (status === 'expired') {
      filtered = filtered.filter(link => link.expiresAt && new Date(link.expiresAt) <= new Date());
    } else if (status === 'inactive') {
      filtered = filtered.filter(link => !link.isActive);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'clicks':
          aValue = a.clickCount;
          bValue = b.clickCount;
          break;
        case 'created':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'updated':
          aValue = new Date(a.updatedAt);
          bValue = new Date(b.updatedAt);
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        default:
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    // Apply pagination
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedLinks = filtered.slice(startIndex, endIndex);

    return {
      links: paginatedLinks,
      total: filtered.length,
      page,
      totalPages: Math.ceil(filtered.length / limit),
      hasMore: endIndex < filtered.length
    };
  }

  async getLinkById(id) {
    return this.links.find(link => link.id === parseInt(id));
  }

  async getLinkByShortCode(shortCode, domainId = null) {
    return this.links.find(link => 
      link.shortCode === shortCode && 
      (domainId ? link.domainId === domainId : !link.domainId)
    );
  }

  async updateLink(id, updates, userId) {
    const linkIndex = this.links.findIndex(link => link.id === parseInt(id));
    if (linkIndex === -1) {
      throw new Error('Link not found');
    }

    const link = this.links[linkIndex];
    if (link.createdBy !== userId) {
      throw new Error('Unauthorized to update this link');
    }

    // Validate updates
    if (updates.originalUrl && !this.validateUrl(updates.originalUrl)) {
      throw new Error('Please enter a valid URL');
    }

    if (updates.customSlug && updates.customSlug !== link.shortCode) {
      this.validateCustomSlug(updates.customSlug);
    }

    if (updates.expiresAt && new Date(updates.expiresAt) <= new Date()) {
      throw new Error('Expiration date must be in the future');
    }

    if (updates.password !== undefined && updates.password && updates.password.length < 4) {
      throw new Error('Password must be at least 4 characters long');
    }

    // Apply updates
    this.links[linkIndex] = {
      ...link,
      ...updates,
      shortCode: updates.customSlug || link.shortCode,
      updatedAt: new Date().toISOString()
    };

    this.saveToStorage();
    return this.links[linkIndex];
  }

  async deleteLink(id, userId) {
    const linkIndex = this.links.findIndex(link => link.id === parseInt(id));
    if (linkIndex === -1) {
      throw new Error('Link not found');
    }

    const link = this.links[linkIndex];
    if (link.createdBy !== userId) {
      throw new Error('Unauthorized to delete this link');
    }

    // Update domain link count if domain specified
    if (link.domainId) {
      const domains = await workingDynamoService.loadDomains();
      const domain = domains.find(d => d.id === link.domainId);
      if (domain) {
        domain.linkCount = Math.max(0, domain.linkCount - 1);
        await workingDynamoService.updateDomain(link.domainId, { linkCount: domain.linkCount });
      }
    }

    this.links.splice(linkIndex, 1);
    this.saveToStorage();
    return true;
  }

  async bulkDelete(ids, userId) {
    const results = [];
    for (const id of ids) {
      try {
        await this.deleteLink(id, userId);
        results.push({ id, success: true });
      } catch (error) {
        results.push({ id, success: false, error: error.message });
      }
    }
    return results;
  }

  async bulkUpdate(ids, updates, userId) {
    const results = [];
    for (const id of ids) {
      try {
        const updated = await this.updateLink(id, updates, userId);
        results.push({ id, success: true, link: updated });
      } catch (error) {
        results.push({ id, success: false, error: error.message });
      }
    }
    return results;
  }

  async recordClick(shortCode, domainId = null, clickData = {}) {
    const link = await this.getLinkByShortCode(shortCode, domainId);
    if (!link) {
      return null;
    }

    // Check if link is active and not expired
    if (!link.isActive) {
      throw new Error('This link has been deactivated');
    }

    if (link.expiresAt && new Date(link.expiresAt) <= new Date()) {
      throw new Error('This link has expired');
    }

    // Update click count
    link.clickCount++;
    link.lastClickAt = new Date().toISOString();
    
    // Simple unique click tracking (in production, use more sophisticated method)
    if (clickData.isUnique) {
      link.uniqueClicks++;
    }

    this.saveToStorage();

    // Update domain total clicks if domain specified
    if (link.domainId) {
      const domains = await workingDynamoService.loadDomains();
      const domain = domains.find(d => d.id === link.domainId);
      if (domain) {
        domain.totalClicks++;
        await workingDynamoService.updateDomain(link.domainId, { totalClicks: domain.totalClicks });
      }
    }

    return link;
  }

  async getLinkAnalytics(id, userId) {
    const link = await this.getLinkById(id);
    if (!link || link.createdBy !== userId) {
      throw new Error('Link not found or unauthorized');
    }

    // In a real implementation, this would fetch detailed analytics
    // For demo purposes, return basic analytics
    return {
      link,
      totalClicks: link.clickCount,
      uniqueClicks: link.uniqueClicks,
      clicksByDate: [], // Would contain actual click data over time
      referrers: [], // Would contain referrer data
      countries: [], // Would contain geographic data
      devices: [] // Would contain device/browser data
    };
  }

  getUserTags(userId) {
    const userLinks = this.links.filter(link => link.createdBy === userId);
    const allTags = userLinks.flatMap(link => link.tags);
    return [...new Set(allTags)].sort();
  }

  getUserStats(userId) {
    const userLinks = this.links.filter(link => link.createdBy === userId);
    
    return {
      totalLinks: userLinks.length,
      totalClicks: userLinks.reduce((sum, link) => sum + link.clickCount, 0),
      activeLinks: userLinks.filter(link => 
        link.isActive && (!link.expiresAt || new Date(link.expiresAt) > new Date())
      ).length,
      expiredLinks: userLinks.filter(link => 
        link.expiresAt && new Date(link.expiresAt) <= new Date()
      ).length
    };
  }

  // Get link by short code (for redirect functionality)
  getByShortCode(shortCode) {
    return this.links.find(link => link.shortCode === shortCode);
  }

  // Increment click count for a link
  incrementClicks(linkId) {
    const link = this.links.find(l => l.id === linkId);
    if (link) {
      link.clickCount = (link.clickCount || 0) + 1;
      link.lastClickAt = new Date().toISOString();
      this.saveToStorage();
    }
    return link;
  }

  // Generate full short URL based on domain
  generateShortUrl(shortCode, domainId) {
    if (domainId) {
      // If custom domain is specified, use it
      const domain = this.getDomainById(domainId);
      if (domain && domain.verified) {
        return `https://${domain.name}/r/${shortCode}`;
      }
    }
    
    // Use default domain (current hostname or localhost for dev)
    const hostname = window.location.hostname;
    const port = window.location.port ? `:${window.location.port}` : '';
    const protocol = window.location.protocol;
    
    return `${protocol}//${hostname}${port}/r/${shortCode}`;
  }

  // Get domain by ID (for URL generation)
  getDomainById(domainId) {
    const workingDynamoService = require('./workingDynamoService');
    return workingDynamoService.workingDynamoService.domains.find(d => d.id === domainId);
  }
}

export const linkService = new LinkService();